import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import KinRadiusMap from './KinRadiusMap';

export default function KinConstructionKit({ kinId, onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trustConfirmed, setTrustConfirmed] = useState(false);
  const [governanceConfirmed, setGovernanceConfirmed] = useState(false);

  // Step 2: Name & Location
  const [kinName, setKinName] = useState('');
  const [kinDescription, setKinDescription] = useState('');
  const [address, setAddress] = useState(user?.location?.address || '');
  const [suburb, setSuburb] = useState(user?.location?.suburb || '');
  const [state, setState] = useState(user?.location?.state || '');
  const [postcode, setPostcode] = useState(user?.location?.postcode || '');
  const [maxRadius, setMaxRadius] = useState(10);
  const [userLocation, setUserLocation] = useState(null);

  // Step 4: Payment Preferences
  const [paymentMethod, setPaymentMethod] = useState('manual');
  const [weeklyAmount, setWeeklyAmount] = useState('');

  // Step 4: Coordinator Compensation
  const [compensationType, setCompensationType] = useState('volunteer');
  const [compensationPercentage, setCompensationPercentage] = useState(5);

  // Step 5: Community Fund
  const [communityFundGoal, setCommunityFundGoal] = useState(500);
  const [communityFundDeadline, setCommunityFundDeadline] = useState(3);

  const totalSteps = 7;

  // Get user's location from profile
  useEffect(() => {
    if (user?.location?.coordinates) {
      const [lng, lat] = user.location.coordinates;
      setUserLocation({ lat, lng });
    }
  }, [user]);

  const handleNext = () => {
    if (step === 1 && !trustConfirmed) {
      setError('Please confirm you understand the Kin trust principles before continuing.');
      return;
    }
    if (step === 2 && !userLocation) {
      setError('Please set your location on the map before continuing.');
      return;
    }
    if (step === 6 && !governanceConfirmed) {
      setError('Please confirm you understand the governance principles before continuing.');
      return;
    }
    setError(null);
    if (step < totalSteps) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!kinName || kinName.trim() === '') {
      setError('Please enter a name for your Kin');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const groupRes = await fetch('/api/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: kinName,
          maxRadiusKm: maxRadius,
          address,
          suburb,
          state,
          postcode,
          location: userLocation,
          paymentMethod,
          compensationType,
          compensationPercentage,
          communityFundGoal,
          communityFundDeadline,
          settings: {
            paymentMethod
          }
        })
      });

      if (!groupRes.ok) {
        const errorData = await groupRes.json();
        throw new Error(errorData.error || 'Failed to create Kin');
      }

      const groupData = await groupRes.json();

      if (onComplete) {
        onComplete(groupData);
      } else {
        navigate('/kin-dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if user already has a Kin
  useEffect(() => {
    const checkKin = async () => {
      try {
        const res = await fetch('/api/groups/my', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.group) {
            navigate('/kin-dashboard');
            return;
          }
        }
      } catch (err) {
        console.error('Failed to check Kin:', err);
      }
    };
    checkKin();
  }, [navigate]);

  const renderStepIndicator = () => {
    return (
      <div className="flex items-center justify-center mb-8">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <React.Fragment key={i}>
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                i === step
                  ? 'bg-green-600 text-white'
                  : i < step
                  ? 'bg-green-200 text-green-800'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {i < step ? '✓' : i}
            </div>
            {i < 7 && (
              <div
                className={`w-16 h-0.5 ${
                  i < step ? 'bg-green-600' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ===== STEP 1: TRUST FIRST =====
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-amber-800 font-semibold">🔑 Trust First</p>
        <p className="text-sm text-amber-700 mt-1">
          A Kin works best when members already know and trust each other.
          Think of it as a group of friends, family, or neighbours who want
          to save money on food together.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 font-medium text-sm">✅ Your Kin should be:</p>
          <ul className="text-sm text-green-700 space-y-1 mt-2">
            <li>• People you already know</li>
            <li>• Friends, family, or neighbours</li>
            <li>• People who live near you</li>
            <li>• People who will contribute fairly</li>
          </ul>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium text-sm">❌ A Kin should NOT be:</p>
          <ul className="text-sm text-red-700 space-y-1 mt-2">
            <li>• Strangers pooling money</li>
            <li>• A way to get free food from others</li>
            <li>• People who don't communicate</li>
          </ul>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 font-medium text-sm">💡 Your role as Kin Coordinator:</p>
        <ul className="text-sm text-blue-700 space-y-1 mt-2">
          <li>• Organise orders for the group</li>
          <li>• Communicate clearly with members</li>
          <li>• Ensure fair contributions</li>
          <li>• If you step down, members vote on a new coordinator</li>
        </ul>
      </div>

      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
        <input
          type="checkbox"
          checked={trustConfirmed}
          onChange={(e) => setTrustConfirmed(e.target.checked)}
          className="w-5 h-5 text-green-600"
        />
        <span className="text-sm text-gray-700">
          I understand that a Kin is built on trust, and I will only invite people I already know and trust.
        </span>
      </label>
    </div>
  );

  // ===== STEP 2: NAME & LOCATION =====
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 font-semibold">📍 Location First</p>
        <p className="text-sm text-blue-700 mt-1">
          Your Kin's location determines who can join. Drag the marker on the map or search for an address,
          then adjust the radius to define your Kin's service area.
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Kin Name *
        </label>
        <input
          type="text"
          value={kinName}
          onChange={(e) => setKinName(e.target.value)}
          placeholder="e.g., Bendigo Foodies"
          className="w-full border rounded-md px-4 py-2"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Choose a name that reflects your group's character and location.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-gray-700 font-medium mb-1">
            Address *
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street address"
            className="w-full border rounded-md px-4 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Suburb *
          </label>
          <input
            type="text"
            value={suburb}
            onChange={(e) => setSuburb(e.target.value)}
            placeholder="Suburb"
            className="w-full border rounded-md px-4 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            State *
          </label>
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="State"
            className="w-full border rounded-md px-4 py-2"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-medium mb-1">
            Postcode *
          </label>
          <input
            type="text"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Postcode"
            className="w-full border rounded-md px-4 py-2"
            required
          />
        </div>
      </div>

      {/* Map Component */}
      <KinRadiusMap
        initialCenter={userLocation ? [userLocation.lat, userLocation.lng] : [-37.8136, 144.9631]}
        initialRadius={maxRadius}
        onChange={(data) => {
          setUserLocation(data.center);
          setMaxRadius(data.radius);
          if (data.address) {
            const parts = data.address.split(',');
            if (parts.length >= 3) {
              setAddress(parts[0].trim());
              setSuburb(parts[1]?.trim() || '');
              setState(parts[2]?.trim() || '');
            }
          }
        }}
      />

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          💡 The green circle shows your Kin's reach. Members must live within this radius to join.
          Drag the marker or click the map to adjust your exact location.
        </p>
      </div>
    </div>
  );

  // ===== STEP 3: INVITE MEMBERS =====
  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          🎉 Now it's time to invite members you trust.
        </p>
        <p className="text-sm text-blue-600 mt-1">
          After creating your Kin, you'll get a unique invite link to share
          with friends and family.
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <div className="text-6xl mb-4">👥</div>
        <h3 className="text-lg font-semibold mb-2">Invite People You Know</h3>
        <ul className="text-left text-sm text-gray-600 space-y-2 max-w-md mx-auto">
          <li>• Share your invite link with friends, family, or neighbours</li>
          <li>• Only people you already know and trust</li>
          <li>• Members within {maxRadius}km will be added automatically</li>
        </ul>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          💡 A Kin of 5-10 households works best. Start small and grow naturally.
        </p>
      </div>
    </div>
  );

  // ===== STEP 4: COORDINATOR COMPENSATION =====
  const renderStep4 = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">🧭 Coordinator Compensation</h2>
      <p className="text-gray-600">
        Choose how the Kin Coordinator is compensated for their work.
      </p>

      <div className="space-y-3">
        <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
          <input
            type="radio"
            name="compensationType"
            value="volunteer"
            checked={compensationType === 'volunteer'}
            onChange={(e) => setCompensationType(e.target.value)}
            className="mt-1"
          />
          <div>
            <span className="font-medium">Volunteer</span>
            <p className="text-sm text-gray-500">
              The Coordinator does this for free. No compensation is taken from orders.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
          <input
            type="radio"
            name="compensationType"
            value="food"
            checked={compensationType === 'food'}
            onChange={(e) => setCompensationType(e.target.value)}
            className="mt-1"
          />
          <div>
            <span className="font-medium">Food Compensation</span>
            <p className="text-sm text-gray-500">
              A percentage of each order goes towards the Coordinator's own food purchases.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
          <input
            type="radio"
            name="compensationType"
            value="nfp"
            checked={compensationType === 'nfp'}
            onChange={(e) => setCompensationType(e.target.value)}
            className="mt-1"
          />
          <div>
            <span className="font-medium">Community Donation</span>
            <p className="text-sm text-gray-500">
              A percentage of each order goes to the Community Fund for group activities or donations.
            </p>
          </div>
        </label>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Compensation Percentage
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={compensationPercentage}
            onChange={(e) => setCompensationPercentage(parseInt(e.target.value))}
            className="flex-1"
          />
          <span className="text-lg font-bold text-green-600 min-w-[60px]">
            {compensationPercentage}%
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {compensationPercentage}% of each order will be set aside for the coordinator.
        </p>
      </div>
    </div>
  );

  // ===== STEP 5: COMMUNITY FUND =====
  const renderStep5 = () => (
    <div className="space-y-4">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-purple-800 font-semibold">🎉 Community Fund</p>
        <p className="text-sm text-purple-600 mt-1">
          The Community Fund is a pool of money that the group can use for
          celebrations, community projects, or supporting the NFP coordinator.
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Community Fund Goal
        </label>
        <div className="flex items-center gap-4">
          <input
            type="number"
            min="100"
            max="2000"
            step="50"
            value={communityFundGoal}
            onChange={(e) => setCommunityFundGoal(parseInt(e.target.value))}
            className="w-32 border rounded-md px-4 py-2"
          />
          <span className="text-gray-500">dollars</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          How much do you need to fund your community projects or celebrations?
        </p>
      </div>

      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Timeframe
        </label>
        <div className="flex items-center gap-4">
          <input
            type="number"
            min="1"
            max="12"
            step="1"
            value={communityFundDeadline}
            onChange={(e) => setCommunityFundDeadline(parseInt(e.target.value))}
            className="w-32 border rounded-md px-4 py-2"
          />
          <span className="text-gray-500">months</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          When you reach your goal, the Community Fund is available.
        </p>
      </div>
    </div>
  );

  // ===== STEP 6: GOVERNANCE & DISPUTE RESOLUTION =====
  const renderStep6 = () => (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-amber-800 font-semibold">⚖️ How Your Kin Resolves Issues</p>
        <p className="text-sm text-amber-700 mt-1">
          Your Kin is a self-governing group. Our Pickup provides the tools,
          but the group decides how to manage itself.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 font-medium text-sm">✅ What Our Pickup DOES do:</p>
          <ul className="text-sm text-blue-700 space-y-1 mt-2">
            <li>• Provide the technology platform</li>
            <li>• Facilitate fair voting processes</li>
            <li>• Enable transparent fund tracking</li>
            <li>• Support winding up if needed</li>
          </ul>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium text-sm">❌ What Our Pickup does NOT do:</p>
          <ul className="text-sm text-red-700 space-y-1 mt-2">
            <li>• Manage Kin funds</li>
            <li>• Make decisions for the group</li>
            <li>• Mediate disputes between members</li>
            <li>• Take sides in disagreements</li>
          </ul>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <p className="font-medium text-gray-800 text-sm">⚖️ How issues are resolved:</p>
        <ol className="text-sm text-gray-600 space-y-2 mt-2 list-decimal list-inside">
          <li>The Kin Coordinator is responsible for day-to-day decisions</li>
          <li>If a member has a concern, they raise it with the coordinator first</li>
          <li>If the coordinator is the problem, members vote for a new coordinator</li>
          <li>If the Kin cannot agree, it can be dissolved and funds distributed</li>
        </ol>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="text-green-800 font-medium text-sm">🛡️ Important Notice:</p>
        <ul className="text-sm text-green-700 space-y-1 mt-2">
          <li>• This is NOT a financial product or investment scheme</li>
          <li>• This is a group of friends buying food together</li>
          <li>• All transactions are tracked through the banking system</li>
          <li>• Individual contributions are under $10,000 per year</li>
        </ul>
      </div>

      <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition">
        <input
          type="checkbox"
          checked={governanceConfirmed}
          onChange={(e) => setGovernanceConfirmed(e.target.checked)}
          className="w-5 h-5 text-green-600"
        />
        <span className="text-sm text-gray-700">
          I understand the governance principles and how issues are resolved within the Kin.
        </span>
      </label>
    </div>
  );

  // ===== STEP 7: LAUNCH =====
  const renderStep7 = () => (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-green-600">
          You're ready to launch your Kin!
        </h2>
        <p className="text-gray-600 mt-2">
          You've built a foundation of trust. Now it's time to start saving together.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
          <span className="text-green-600 text-xl">✓</span>
          <div>
            <span className="font-medium">Trust confirmed</span>
            <p className="text-sm text-gray-500">
              You understand that a Kin works best with people you know and trust.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
          <span className="text-blue-600 text-xl">✓</span>
          <div>
            <span className="font-medium">Your Kin is ready</span>
            <p className="text-sm text-gray-500">
              Name, location, and preferences are set.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
          <span className="text-purple-600 text-xl">✓</span>
          <div>
            <span className="font-medium">Community Fund is active</span>
            <p className="text-sm text-gray-500">
              Members will see progress towards your group goals.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
          <span className="text-amber-600 text-xl">📖</span>
          <div>
            <span className="font-medium">Governance is in place</span>
            <p className="text-sm text-gray-500">
              If you ever step down, members will vote on a new coordinator.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded-lg">
          {error}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 transition"
      >
        {loading ? 'Creating Kin...' : '🚀 Launch My Kin!'}
      </button>

      <p className="text-xs text-gray-400 text-center">
        By launching your Kin, you agree to be a responsible coordinator and
        to treat your members fairly.
      </p>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          🏗️ Kin Construction Kit
        </h1>
        <p className="text-gray-500">
          Build your buying group in 7 simple steps
        </p>
      </div>

      {renderStepIndicator()}

      <div className="min-h-[400px]">
        {renderStep()}
      </div>

      <div className="flex justify-between mt-8 pt-4 border-t">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2 border rounded-md hover:bg-gray-50 disabled:opacity-50 transition"
        >
          Back
        </button>
        <div className="text-sm text-gray-400 self-center">
          Step {step} of {totalSteps}
        </div>
        {step < totalSteps && (
          <button
            onClick={handleNext}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
          >
            Continue →
          </button>
        )}
        {step === totalSteps && (
          <div className="w-[100px]" />
        )}
      </div>
    </div>
  );
}
